import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from '@martinjlowm/react-hook-form';
import { mdiFormatLetterCaseLower, mdiFormatLetterCaseUpper, mdiNotEqualVariant, mdiNumeric, mdiSymbol } from '@mdi/js';
import Icon from '@mdi/react';
import { D, pipe } from '@mobily/ts-belt';
import { Check as CheckIcon, ShortText as ShortTextIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Avatar,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { EventHandler, FC, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';
import z from 'zod';

import { PasswordPolicyType } from '@/types/ui-auth-rust-types';

import { setPasswordSchema } from './schemas';

const i18nDefaultValues = {
  'auth:requireEquality': 'The passwords must match',
  'auth:minimumLength': 'Must have a minimum length of {{length}}',
  'auth:requireUppercase': 'Must include an uppercase letter',
  'auth:requireLowercase': 'Must include a lowercase letter',
  'auth:requireNumbers': 'Must contain a number (0-9)',
  'auth:requireSymbols': 'Must contain a special character (!@#$%^&* etc.)',
};

export type FormValues = z.infer<typeof setPasswordSchema>;

export interface Properties {
  onSubmit: (form: Omit<FormValues, 'confirmPassword'>) => void;
  onCancel: () => void;
  defaultValues?: Partial<FormValues>;
  passwordPolicy: PasswordPolicyType;
}

const resolver = zodResolver(setPasswordSchema);

const validationI18nKeyMapping = {
  minimumLength: 'auth:minimumLength',
  requireUppercase: 'auth:requireUppercase',
  requireLowercase: 'auth:requireLowercase',
  requireNumbers: 'auth:requireNumbers',
  requireSymbols: 'auth:requireSymbols',
  requireEquality: 'auth:requireEquality',
};

const validationIconMapping = {
  minimumLength: () => <ShortTextIcon />,
  requireUppercase: () => <Icon size={1} path={mdiFormatLetterCaseUpper} />,
  requireLowercase: () => <Icon size={1} path={mdiFormatLetterCaseLower} />,
  requireNumbers: () => <Icon size={1} path={mdiNumeric} />,
  requireSymbols: () => <Icon size={1} path={mdiSymbol} />,
  requireEquality: () => <Icon size={1} path={mdiNotEqualVariant} />,
};

const staticPasswordPolicies = {
  requireUppercase: z.string().regex(/(?=.*[A-Z])/),
  requireLowercase: z.string().regex(/(?=.*[a-z])/),
  requireNumbers: z.string().regex(/(?=.*\d)/),
  requireSymbols: z.string().regex(/(.*[\^\$\*\.\[\]\{\}\(\)\?\-\+\"\!\@\#\%\&\\/\,\>\<\'\:\;\|\_\~\=\`].*)/),
};

const SetPassword: FC<Properties> = ({
  onSubmit,
  onCancel,
  defaultValues,
  passwordPolicy: { temporaryPasswordValidityDays: _, ...passwordPolicy },
}) => {
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();

  const policyValidations = {
    ...staticPasswordPolicies,
    minimumLength: z.string().min(passwordPolicy.minimumLength),
  };

  const {
    register,
    handleSubmit,
    getValues,
    setError,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues,
    resolver,
    criteriaMode: 'all',
  });

  const email = getValues('email');
  const password = getValues('password');
  const challengeCode = getValues('challengeCode');
  const session = getValues('session');

  useEffect(() => {
    const types = pipe(
      policyValidations,
      D.filterWithKey((policyName, policy) => {
        if (!passwordPolicy[policyName]) {
          return false;
        }

        return !policy.safeParse(password).success;
      }),
      D.mapWithKey((policyName) => i18nDefaultValues[`auth:${policyName}`]),
    );

    if (D.isEmpty(types)) {
      return;
    }

    const existingErrors = errors.password?.types;
    if (existingErrors && D.keys(types).every((key) => key in existingErrors)) {
      return;
    }

    setError('password', { ...errors.password, types: { ...errors.password?.types, ...types } });
  }, [setError, password, errors.password?.types]);

  const toggleShowPassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  const submitForm = useCallback<EventHandler<FormEvent<HTMLFormElement>>>((event) => {
    return handleSubmit(({ confirmPassword: _, ...form }) => {
      return onSubmit(form);
    })(event);
  }, []);

  const hasChallengeCode = !!challengeCode;
  const passwordErrors = useMemo(() => errors?.password?.types || {}, [errors?.password?.types]);

  const passwordPolicyIcons = useMemo(
    () =>
      [...D.keys(passwordPolicy), 'requireEquality' as const].map((key) => {
        const code = key === 'requireEquality' ? 'custom' : key;
        const Icon = code in passwordErrors ? validationIconMapping[key] : () => <CheckIcon />;
        const i18nKey = validationI18nKeyMapping[key] as `auth:${typeof key}`;
        const defaultTranslation = i18nDefaultValues[i18nKey];

        return (
          <Grid item key={key}>
            <Tooltip
              arrow
              title={t(i18nKey, { defaultValue: defaultTranslation, length: passwordPolicy.minimumLength })}
            >
              <Avatar
                sx={{ bgcolor: code in passwordErrors ? theme.palette.error.main : theme.palette.success.main }}
                variant="rounded"
              >
                <Icon />
              </Avatar>
            </Tooltip>
          </Grid>
        );
      }),
    [passwordErrors],
  );

  const [challengeIdentifier, challengeLabel] = match(session)
    .with(null, () => ['verification code', t('auth:verificationCode', 'Verification Code')])
    .otherwise(() => ['temporary password', t('auth:temporaryPassword', 'Temporary Password')]);

  return (
    <form onSubmit={submitForm}>
      <CardHeader
        titleTypographyProps={{ align: 'center' }}
        subheaderTypographyProps={{ align: 'center' }}
        title={t('auth:setPassword', 'Set Password')}
        subheader={
          <Trans i18nKey="auth:pleaseCheckYourEmailChallengeCode">
            {/* @ts-ignore */}
            Please check your e-mail, <strong>{{ email }}</strong>, for a {{ challengeIdentifier }}.
          </Trans>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <input type="hidden" {...register('email')} />
          <input type="hidden" {...register('challengeName')} />
          <Grid item xs={12}>
            <TextField
              fullWidth
              autoFocus={!hasChallengeCode}
              error={'challengeCode' in errors}
              autoComplete="off"
              required
              label={challengeLabel}
              {...register('challengeCode')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              autoFocus={hasChallengeCode}
              fullWidth
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              error={'password' in errors}
              required
              label={t('auth:password', 'Password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton tabIndex={-1} onClick={toggleShowPassword}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...register('password', { deps: ['confirmPassword'] })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              error={'confirmPassword' in errors}
              type={showPassword ? 'text' : 'password'}
              autoComplete="off"
              required
              label={t('auth:confirmPassword', 'Confirm Password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton tabIndex={-1} onClick={toggleShowPassword}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...register('confirmPassword', { deps: ['password'] })}
            />
          </Grid>
          <Grid container item spacing={2} justifyContent={'space-around'} xs={12}>
            {passwordPolicyIcons}
          </Grid>
        </Grid>
      </CardContent>
      <CardActions sx={{ p: 2 }}>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item xs={12}>
            <Button fullWidth disabled={!isValid} variant="contained" color="primary" type="submit">
              {t('auth:setPassword', 'Set Password')}
            </Button>
          </Grid>
          <Grid item>
            <Button color="primary" onClick={onCancel}>
              {t('common:returnToLogin', 'Return to login')}
            </Button>
          </Grid>
        </Grid>
      </CardActions>
    </form>
  );
};

export default SetPassword;
